import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Typography,
  Box,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from '../../contexts/LanguageContext';
import { costCentersService, CostCenterDto } from '../../services/costCenters.service';

interface CostCenterSelectorProps {
  value: number | null;
  onChange: (costCenterId: number | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

const CostCenterSelector: React.FC<CostCenterSelectorProps> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCostCenters = async () => {
    try {
      setLoading(true);
      const data = await costCentersService.getAll(false, true);
      const flatCostCenters = flattenCostCenters(data);
      setCostCenters(flatCostCenters);
    } catch (err) {
      console.error('Failed to load cost centers:', err);
      setCostCenters([]);
    } finally {
      setLoading(false);
    }
  };

  const flattenCostCenters = (centers: CostCenterDto[]): CostCenterDto[] => {
    const result: CostCenterDto[] = [];
    const seen = new Set<number>();

    const flatten = (ctrs: CostCenterDto[]) => {
      ctrs.forEach(center => {
        if (!seen.has(center.costCenterId)) {
          seen.add(center.costCenterId);
          result.push(center);
          if ((center as any).childCostCenters && (center as any).childCostCenters.length > 0) {
            flatten((center as any).childCostCenters);
          }
        }
      });
    };

    flatten(centers);
    return result;
  };

  const filteredCostCenters = costCenters.filter(cc => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      cc.costCenterCode.toLowerCase().includes(search) ||
      cc.costCenterName.toLowerCase().includes(search) ||
      (cc.costCenterNameAr && cc.costCenterNameAr.includes(searchTerm))
    );
  });

  return (
    <FormControl fullWidth={fullWidth} error={error} disabled={disabled} required={required}>
      <InputLabel>{label || (isRTL ? 'مركز التكلفة' : 'Cost Center')}</InputLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        label={label || (isRTL ? 'مركز التكلفة' : 'Cost Center')}
      >
        <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <TextField
            size="small"
            placeholder={isRTL ? 'بحث...' : 'Search...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <MenuItem value="">
          <em>{isRTL ? 'بدون مركز تكلفة' : 'None'}</em>
        </MenuItem>

        {loading && (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>{isRTL ? 'جاري التحميل...' : 'Loading...'}</Typography>
          </MenuItem>
        )}

        {!loading && filteredCostCenters.map((cc) => (
          <MenuItem
            key={cc.costCenterId}
            value={cc.costCenterId}
            sx={{
              display: 'flex',
              gap: 1.5,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: 'primary.main',
                minWidth: '80px',
                fontSize: '0.9rem'
              }}
            >
              {cc.costCenterCode}
            </Typography>
            <Typography component="span">
              {isRTL ? cc.costCenterNameAr : cc.costCenterName}
            </Typography>
          </MenuItem>
        ))}

        {!loading && filteredCostCenters.length === 0 && (
          <MenuItem disabled>
            <em>{isRTL ? 'لا توجد نتائج' : 'No results'}</em>
          </MenuItem>
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default CostCenterSelector;